import { Inbox, Lock, SearchX } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { ListToolbar } from '@/components/list/ListToolbar';
import { useListParams } from '@/components/list/use-list-params';
import { ViewTabs, type ListView } from '@/components/list/ViewTabs';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import type { QueueItem } from '../reports/api-decisions';
import { QueueCards, QueueTable } from './QueueList';
import { useApprovalQueue } from './use-approval-queue';
import { useOnboardingClick } from '@/communications/use-onboarding-click';

type Level = 'first' | 'second';

/** The queue's orders (#952 L9): the longest wait first by default. */
const SORTS = ['waiting:desc', 'waiting:asc', 'total:desc', 'total:asc', 'owner:asc'] as const;
type Sort = (typeof SORTS)[number];

const NO_FILTERS: readonly string[] = [];

/** The search (number, person, reason) and the order, on the queue the server sent whole. */
function shape(items: readonly QueueItem[], q: string, sort: Sort): QueueItem[] {
  const needle = q.trim().toLocaleLowerCase('it');
  const found =
    needle === ''
      ? [...items]
      : items.filter((item) =>
          [item.number, item.owner.displayName, item.title].some((text) =>
            text.toLocaleLowerCase('it').includes(needle),
          ),
        );
  const [field, direction] = sort.split(':');
  const sign = direction === 'asc' ? 1 : -1;
  return found.sort((a, b) => {
    if (field === 'total') return sign * (Number(a.total) - Number(b.total));
    if (field === 'owner')
      return sign * a.owner.displayName.localeCompare(b.owner.displayName, 'it');
    return sign * (a.waitingDays - b.waitingDays);
  });
}

/**
 * «Da approvare» (spec §1.4, §1.5): the reports the caller may decide now, by level. The 2nd-level
 * tab appears only when something waits there: the organisation's setting is not readable by an
 * approver, so a one-approver organisation simply never has a 2nd level.
 */
export function ApprovalsPage() {
  // The email's button lands here with `?onboarding=<step>`: logged once, then removed (#739).
  useOnboardingClick();
  const { t } = useTranslation('expenses');
  const { t: tr } = useTranslation('expenses-reports');
  const titleId = useId();
  const { state, reload } = useApprovalQueue();
  const [chosen, setChosen] = useState<Level>('first');
  const { t: tc } = useTranslation('common');
  const list = useListParams(NO_FILTERS);
  const sort: Sort = SORTS.find((value) => value === list.sort) ?? 'waiting:desc';
  const sortLabels: Record<Sort, string> = {
    'waiting:desc': tr('approvals.sorts.waiting-desc'),
    'waiting:asc': tr('approvals.sorts.waiting-asc'),
    'total:desc': tr('approvals.sorts.total-desc'),
    'total:asc': tr('approvals.sorts.total-asc'),
    'owner:asc': tr('approvals.sorts.owner-asc'),
  };

  let body;
  if (state.status === 'loading') {
    body = (
      <SkeletonRegion label={t('list.loading')} className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((n) => (
          <Skeleton key={n} className="h-12 w-full" />
        ))}
      </SkeletonRegion>
    );
  } else if (state.status === 'forbidden') {
    body = (
      <StateMessage
        role="alert"
        icon={Lock}
        title={t('approvals.noAccess')}
        text={t('approvals.noAccessText')}
      />
    );
  } else if (state.status === 'error') {
    body = (
      <StateMessage
        role="alert"
        tone="error"
        icon={Inbox}
        title={t('approvals.loadError')}
        action={
          <Button type="button" variant="outline" onClick={reload}>
            {t('approvals.retry')}
          </Button>
        }
      />
    );
  } else {
    const { firstLevel, secondLevel } = state.queue;
    const hasSecond = secondLevel.count > 0 || secondLevel.items.length > 0;
    const level: Level = hasSecond ? chosen : 'first';
    const all = (level === 'first' ? firstLevel : secondLevel).items;
    const items = shape(all, list.q, sort);
    const counts: Record<Level, number> = { first: firstLevel.count, second: secondLevel.count };
    const levels: ListView[] = (['first', 'second'] as const).map((key) => ({
      id: key,
      label: t(`approvals.${key}`, { count: counts[key] }),
      params: { level: key },
    }));
    body = (
      <>
        {hasSecond ? (
          <ViewTabs
            label={tr('approvals.levels')}
            views={levels}
            current={{ get: (key) => (key === 'level' ? level : null) }}
            onSelect={(view) => setChosen(view.id as Level)}
          />
        ) : null}
        {all.length > 0 ? (
          <ListToolbar
            label={t('approvals.title')}
            search={{
              label: tr('approvals.search'),
              value: list.q,
              onWrite: (q) => list.set('q', q),
            }}
            fields={[]}
            active={{}}
            onFilter={list.set}
            onClear={list.clear}
            sort={{
              label: tc('list.sort'),
              value: sort,
              onChange: (value) => list.set('sort', value === 'waiting:desc' ? null : value),
              options: SORTS.map((value) => ({ value, label: sortLabels[value] })),
            }}
          />
        ) : null}
        {all.length === 0 ? (
          <StateMessage
            icon={Inbox}
            title={tr('approvals.empty.title')}
            text={tr('approvals.empty.text')}
          />
        ) : items.length === 0 ? (
          <StateMessage
            icon={SearchX}
            title={tr('approvals.noResults.title')}
            text={tr('approvals.noResults.text')}
            action={
              <Button type="button" variant="outline" onClick={list.clear}>
                {tr('approvals.noResults.clear')}
              </Button>
            }
          />
        ) : (
          <>
            <QueueTable items={items} />
            <QueueCards items={items} />
          </>
        )}
      </>
    );
  }

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <PageHead titleId={titleId} title={t('approvals.title')} summary={tr('approvals.scope')} />
      {body}
    </section>
  );
}
